import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type ReserveTrip, type Person } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column, type BadgeTone } from '@/components/ui';
import { IconTrips } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { personName, dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer } from '@/components/admin/Drawer';
import { TripDrawer } from '@/components/admin/TripDrawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

const PAGE = 25;

/** The release-state filter the segmented control drives, 1:1 with the API's
 *  `released` query param ('' = all). A reserve is HELD ("upcoming") at booking
 *  and the trip-svc scheduler RELEASES it into dispatch ~RESERVE_LEAD_MINUTES
 *  before pickup, stamping reserve_released_at. */
type ReleaseFilter = '' | 'upcoming' | 'released';

// Reserve trip status → label + tone (canonical Telride booking_statuses). A
// held reserve sits at Requested until the scheduler releases it and a driver
// accepts; mirrors the Trips page map so the two read identically.
const STATUS: Record<number, { label: string; tone: BadgeTone }> = {
  1: { label: 'Requested', tone: 'info' },
  2: { label: 'Arrived', tone: 'info' },
  3: { label: 'Started', tone: 'warn' },
  5: { label: 'Completed', tone: 'ok' },
  6: { label: 'Cancelled', tone: 'danger' },
  7: { label: 'Cancelled (driver)', tone: 'danger' },
};

/** Reserve Trips — the scheduled, book-in-advance rides (trip-svc's
 *  trip_type=14). A RESERVE is HELD at booking (status Requested, no driver, no
 *  trip.requested event) and the trip-svc scheduler RELEASES it into the normal
 *  sequential-offer dispatch ~RESERVE_LEAD_MINUTES before its scheduled pickup,
 *  stamping reserve_released_at. This page lists them with that schedule + a
 *  held/released roll-up, and reuses the Trips detail drawer for the full row.
 *
 *  An operator can ASSIGN a held reserve to a specific driver (reserves.assign):
 *  it proxies trip-svc's admin-assign, which OFFERS the trip to that driver —
 *  they accept/reject it in their Bookings screen and get an FCM. driver_id is
 *  set only once they ACCEPT; until then the reserve shows "Offered (pending)".
 *
 *  DEFENSIVE: the `trips` table is owned by trip-svc and may not be in this
 *  Postgres yet (pre-cutover from Laravel/MySQL); the API returns
 *  available:false + a note and the page shows a clean "not yet available"
 *  state, identical to the Trips page. */
export function ReserveTrips() {
  const [q, setQ] = useState('');
  const [released, setReleased] = useState<ReleaseFilter>('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [assigning, setAssigning] = useState<ReserveTrip | null>(null);
  const canAssign = useAdminPermission('reserves.assign');

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'reserves', q, released, offset],
    queryFn: ({ signal }) =>
      adminApi.reserves(
        { q, released: released === '' ? undefined : released, limit: PAGE, offset },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;
  const stats = data?.stats;

  const columns: Array<Column<ReserveTrip>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    { key: 'trip_id', header: 'Reserve', cell: (r) => <span className="text-slate-200">{r.trip_id || '—'}</span> },
    { key: 'cust', header: 'Customer', cell: (r) => <span className="metric text-xs text-slate-300">#{r.customer_id}</span> },
    {
      key: 'drv',
      header: 'Driver',
      cell: (r) =>
        r.driver_id ? (
          <span className="metric text-xs text-slate-300">#{r.driver_id}</span>
        ) : r.assigned_driver_id ? (
          <Badge tone="info" dot>Offered #{r.assigned_driver_id} · pending</Badge>
        ) : (
          <Badge tone="warn">Unassigned</Badge>
        ),
    },
    {
      key: 'pickup_at',
      header: 'Pickup scheduled',
      cell: (r) => <span className="text-xs text-slate-300">{dateTime(r.scheduled_pickup_at)}</span>,
    },
    { key: 'pickup', header: 'Pickup', cell: (r) => <span className="truncate text-xs text-slate-300" title={r.pickup_address}>{r.pickup_address || '—'}</span> },
    {
      key: 'release',
      header: 'Dispatch',
      cell: (r) =>
        r.released ? (
          <Badge tone="ok" dot>Released</Badge>
        ) : (
          <Badge tone="info" dot>Upcoming</Badge>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => {
        const s = STATUS[r.status] ?? { label: `#${r.status}`, tone: 'neutral' as BadgeTone };
        return <Badge tone={s.tone}>{s.label}</Badge>;
      },
    },
    { key: 'total', header: 'Total', align: 'right', cell: (r) => <span className="metric text-xs">{r.total ? `$${r.total.toFixed(2)}` : '—'}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => {
        // Assignable while NO driver has accepted yet — including a RELEASED
        // reserve whose auction lapsed un-accepted (trip-svc's released rescue
        // revokes any in-flight auction before hand-assigning) and a pending
        // offer the driver is ignoring (trip-svc allows re-offering to someone
        // else until a driver actually accepts). Only an accepted reserve
        // (driver_id set) is out of the operator's hands.
        const assignable = !r.driver_id;
        return (
          <div className="flex justify-end gap-2">
            {canAssign && assignable ? (
              <button
                type="button"
                onClick={() => setAssigning(r)}
                className="rounded-md border border-brand-500/40 bg-brand-500/10 px-2 py-1 text-xs font-medium text-brand-300 hover:bg-brand-500/20"
              >
                Assign
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setSelected(r.id)}
              className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
            >
              View
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Reserve trips"
        subtitle="Scheduled, book-in-advance rides — held until the scheduler releases them into dispatch near pickup."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search reserves…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Reserve trips not yet in Postgres"
            description={
              data?.note ??
              'The trips table is owned by trip-svc and is still in Laravel/MySQL pre-cutover. This page lights up automatically once the table lands here.'
            }
            icon={<IconTrips className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatTile label="Total reserves" value={stats?.total ?? 0} icon={<IconTrips className="h-4 w-4" />} />
            <StatTile
              label="Upcoming (held)"
              value={stats?.upcoming ?? 0}
              tone="info"
              hint="Not yet released into dispatch"
            />
            <StatTile
              label="Released"
              value={stats?.released ?? 0}
              tone="ok"
              hint="Pushed into the offer flow"
            />
          </div>

          <div className="mb-3 flex items-center justify-between gap-3">
            <ReleaseFilterControl value={released} onChange={(v) => { setReleased(v); setOffset(0); }} fetching={isFetching} />
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading reserve trips…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load reserve trips" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<ReserveTrip>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No reserve trips"
                      description="No scheduled (trip_type=14) rides match this filter."
                      icon={<IconTrips className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected !== null ? <TripDrawer id={selected} onClose={() => setSelected(null)} /> : null}
      {assigning ? <AssignDriverModal reserve={assigning} onClose={() => setAssigning(null)} /> : null}
    </AdminPage>
  );
}

// ─── assign-driver modal (driver picker) ─────────────────────

/** AssignDriverModal — search drivers and OFFER the reserve to one. On success
 *  the reserve flips to "Offered (pending)" and the driver gets an FCM + a
 *  Bookings entry to accept/reject. Reuses the drivers list endpoint to search. */
function AssignDriverModal({ reserve, onClose }: { reserve: ReserveTrip; onClose: () => void }) {
  const qc = useQueryClient();
  const [dq, setDq] = useState('');
  const [picked, setPicked] = useState<Person | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['admin', 'drivers', 'picker', dq],
    queryFn: ({ signal }) => adminApi.drivers({ q: dq, limit: 8, offset: 0 }, signal),
    placeholderData: (prev) => prev,
  });
  const drivers = data?.items ?? [];

  const mutation = useMutation({
    mutationFn: () => adminApi.assignReserveDriver(reserve.id, picked!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'reserves'] });
      onClose();
    },
  });

  return (
    <Drawer
      title={`Assign reserve ${reserve.trip_id || `#${reserve.id}`}`}
      subtitle="Offer this scheduled trip to a driver — they accept or reject it in their app."
      onClose={onClose}
    >
      <div className="space-y-4">
        <SearchBox value={dq} onChange={setDq} placeholder="Search drivers by name / phone / id…" />

        <Card flush>
          {isFetching && drivers.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Spinner label="Searching drivers…" />
            </div>
          ) : drivers.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted">No drivers match.</div>
          ) : (
            <ul className="divide-y divide-ink-800">
              {drivers.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => setPicked(d)}
                    className={
                      'flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-ink-800 ' +
                      (picked?.id === d.id ? 'bg-brand-500/10' : '')
                    }
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-slate-100">{personName(d)}</span>
                      <span className="metric block text-[11px] text-muted">
                        #{d.id} · {d.country_code}{d.phone}
                        {d.service_line ? ` · ${d.service_line}` : ''}
                      </span>
                    </span>
                    {picked?.id === d.id ? <Badge tone="ok">Selected</Badge> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!picked || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="btn-primary"
          >
            {mutation.isPending ? 'Assigning…' : picked ? `Offer to ${personName(picked)}` : 'Pick a driver'}
          </button>
          {mutation.isError ? (
            <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}

// ─── release-state filter (segmented control) ────────────────

/** A small segmented All / Upcoming / Released filter over the reserve list,
 *  bound 1:1 to the API's `released` query param. Mirrors the segmented control
 *  on the Trip-types / Quests pages so the whole admin reads consistently. */
function ReleaseFilterControl({
  value,
  onChange,
  fetching,
}: {
  value: ReleaseFilter;
  onChange: (v: ReleaseFilter) => void;
  fetching?: boolean;
}) {
  const opts: Array<{ key: ReleaseFilter; label: string }> = [
    { key: '', label: 'All' },
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'released', label: 'Released' },
  ];
  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex rounded-lg border border-ink-700 bg-ink-900 p-0.5">
        {opts.map((o) => (
          <button
            key={o.key || 'all'}
            type="button"
            onClick={() => onChange(o.key)}
            className={
              'rounded-md px-3 py-1 text-xs font-medium transition-colors ' +
              (value === o.key
                ? 'bg-brand-500 text-white'
                : 'text-text-secondary hover:bg-hover hover:text-slate-100')
            }
          >
            {o.label}
          </button>
        ))}
      </div>
      {fetching ? <Spinner /> : null}
    </div>
  );
}
